import { writable, get, type Writable } from 'svelte/store';
import { isEmpty } from 'lodash-es';
import type { Conversation, EventStatus, ChatEvent, StoreWithGet } from '../types';

/**
 * get 메서드를 가진 스토어 생성 헬퍼
 */
export function createStore<T>(initialState: T): StoreWithGet<T> {
    const store: Writable<T> = writable(initialState);

    return {
        subscribe: store.subscribe,
        set: store.set,
        update: store.update,
        getState: () => get(store),
    };
}

/**
 * 단일 상태 업데이트 함수
 * @param items - 업데이트할 아이템 ex) conversation.events, conversation.results?.tasks
 * @param status - 업데이트할 상태 ex) 'COMPLETE'
 * @returns 업데이트된 새 객체/배열
 */
const updateStatus = <T extends { status: EventStatus | string }>(
    items: Record<string, { status: EventStatus | string; details: string }> | T[] | undefined,
    status: EventStatus,
): Record<string, { status: EventStatus; details: string }> | T[] | undefined => {
    if (!items) return undefined;

    if (Array.isArray(items)) {
        return items.map(item => ({ ...item, status })) as T[];
    }

    if (typeof items === 'object') {
        return Object.fromEntries(Object.entries(items).map(([key, item]) => [key, { ...item, status }]));
    }

    return undefined;
};

/**
 * 모든 이벤트의 상태를 업데이트하는 함수
 * @param conversation - 채팅 데이터
 * @param status - 업데이트할 상태 ex) 'COMPLETE'
 * @returns conversation 객체
 */
export const updateAllEventStatus = (conversation: Conversation, status: EventStatus): Conversation => {
    if (!conversation) return conversation;

    // 모든 이벤트 상태를 한번에 업데이트하여 반환
    return {
        ...conversation,
        // 이벤트 상태 업데이트
        events: updateStatus(conversation.events, status) as Record<string, { status: EventStatus; details: string }> | undefined,
        // results 객체 업데이트 (태스크 포함)
        results: conversation.results
            ? {
                  ...conversation.results,
                  tasks: conversation.results.tasks ? (updateStatus(conversation.results.tasks, status) as AistudioChatTaskDtoV2[]) : [],
              }
            : undefined,
        // 처리된 이벤트 상태 업데이트
        processedEvents: conversation.processedEvents ? (updateStatus(conversation.processedEvents, status) as ChatEvent[] | undefined) : undefined,
    };
};

/**
 * 대화 중단 시 ONGOING 상태 이벤트의 진행 문구("답변 중" 등) 잔상 제거
 * COMPLETE 상태 이벤트는 유지 ("자료를 모두 확인하였습니다." 등)
 * @param conversation - 대화 객체
 */
export const clearOngoingProgressDetails = (conversation: Conversation): Conversation => {
    if (!conversation) return conversation;

    const isOngoing = (status?: string) => status === 'ONGOING';

    let result: Conversation = { ...conversation };

    // conversation.details 초기화 (ONGOING일 때만)
    if (isOngoing(result.status)) {
        result.details = '';
    }

    // events 초기화 (ONGOING만)
    if (result.events) {
        result.events = Object.fromEntries(Object.entries(result.events).map(([key, event]) => [key, isOngoing(event.status) ? { ...event, details: '' } : event]));
    }

    // processedEvents 초기화 (ONGOING만)
    if (Array.isArray(result.processedEvents)) {
        result.processedEvents = result.processedEvents.map(event => {
            if (!isOngoing(event.status)) return event;
            if (typeof event.details === 'string') return { ...event, details: '' } as ChatEvent;
            return { ...event, details: { ...event.details, message: '', searchMessage: '' } } as ChatEvent;
        });
    }

    return result;
};

/**
 * 정밀 검색 타입에 대한 처리된 이벤트를 생성합니다
 * @param conversation - 대화 객체
 * @returns 처리된 이벤트 배열
 */
const createAdvancedProcessedEvents = (conversation: Conversation): ChatEvent[] => {
    if (!conversation) return [];

    // 1) TASKS 이벤트는 live SSE / run history 모두에서 canonical source. tasks만으로 ActionTracker를 복원한다.
    const hasSearchResults = (task: AistudioChatTaskDtoV2): boolean => Array.isArray(task.results?.search?.results) && (task.results?.search?.results?.length ?? 0) > 0;
    const hasQueries = (task: AistudioChatTaskDtoV2): boolean => typeof task.details === 'object' && task.details !== null && Array.isArray(task.details.queries) && task.details.queries.length > 0;
    const filteredTasks = conversation.results?.tasks?.filter(task => task.status !== 'NOT_YET' && ((task.details && !isEmpty(task.details)) || hasSearchResults(task) || hasQueries(task))) || [];
    if (filteredTasks.length) {
        return filteredTasks;
    }

    // 2) tasks가 없고 대화 레벨 details만 있으면 단일 항목으로 표시
    if (conversation.details && !isEmpty(conversation.details)) {
        return [
            {
                status: conversation.status || 'ONGOING',
                details: conversation.details,
            },
        ];
    }

    return [];
};

/**
 * 빠른 검색 타입에 대한 처리된 이벤트를 생성합니다
 * @param conversation - 대화 객체
 * @returns 처리된 이벤트 배열
 */
const createQuickProcessedEvents = (conversation: Conversation): ChatEvent[] => {
    if (!conversation || !conversation.events || isEmpty(conversation.events)) {
        return [];
    }
    // 정렬 없이 삽입 순서 유지, details가 있는 항목만 반환
    return Object.values(conversation.events).filter(event => {
        const eventDetails: any = (event as any).details;
        if (!eventDetails) return false;
        if (typeof eventDetails === 'string') return eventDetails.trim().length > 0;
        return !!eventDetails.message && String(eventDetails.message).trim().length > 0;
    });
};

/**
 * 대화에서 processedEvents를 생성하는 함수
 * @param conversation - 대화 객체
 * @returns 처리된 이벤트 배열
 */
export const createProcessedEvents = (conversation: Conversation): ChatEvent[] => {
    if (!conversation) return [];

    const searchType = conversation.chatSearchType as 'QUICK' | 'ADVANCED';

    if (searchType === 'ADVANCED') {
        return createAdvancedProcessedEvents(conversation);
    } else if (searchType === 'QUICK') {
        return createQuickProcessedEvents(conversation);
    }

    return [];
};
